import { useLockFn, useRequest } from 'ahooks'
import { Button, Dropdown, Menu, MenuProps, Modal } from 'antd'
import { isNil } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { OKRList, PortfolioList, ProjectList } from '@/components'
import { CustomerList } from '@/modules/customer/customer-home.component'
import { DevlopList } from '@/modules/devlop/devlop-home.component'
import { ProductionList } from '@/modules/production-project/production-home.component'
import { TeamList } from '@/modules/team/team-list'
import { ReportConfigurationModuleField, ReportSnapshot, ResourceType } from '@/schemas'

export enum PpReportSnapshotOperation {
  DELETE = 0,
  ADD = 1,
}

interface SnapshotsRelationCreatorProps {
  rootSelfId: string
  selfId: string
  snapshot: ReportSnapshot
  depth: number
  fields?: ReportConfigurationModuleField[]
  onSuccess: () => void
  disabledSelfIds: (string | number)[]
}

export const SnapshotsRelationCreator: React.FC<SnapshotsRelationCreatorProps> = ({
  rootSelfId,
  selfId,
  snapshot,
  depth,
  onSuccess,
  disabledSelfIds,
  fields = [],
  children,
}) => {
  const { t } = useTranslation()
  const [targetType, setTargetType] = React.useState<string | null>(null)

  const { run: runCreate } = useRequest(
    (data) => ({
      url: '/api/report/createPortfolioReportSnapshot',
      method: 'post',
      data: {
        rootSelfId,
        parentId: snapshot.id || 'root',
        path: snapshot.path || 'root',
        reportId: snapshot.reportId,
        moduleId: snapshot.moduleId,
        selfId: data.selfId,
        name: data.name,
        departmentId: data.departmentId,
        email: data.email,
        risk: data.risk,
        type: data.type,
        startDate: data.startDate,
        endDate: data.endDate,
        progress: data.progress,
        status: data.status,
        operation: PpReportSnapshotOperation.ADD,
      },
    }),
    { manual: true, onSuccess }
  )

  const runCreateLocked = useLockFn(runCreate)

  const { run: runDelete } = useRequest(
    {
      url: '/api/report/createPortfolioReportSnapshot',
      method: 'post',
      data: {
        id: snapshot.id,
        rootSelfId,
        parentId: snapshot.parentId,
        path: snapshot.path,
        reportId: snapshot.reportId,
        moduleId: snapshot.moduleId,
        selfId: snapshot.selfId,
        name: snapshot.name,
        departmentId: snapshot.departmentId,
        email: snapshot.email,
        risk: snapshot.risk,
        type: snapshot.type,
        startDate: snapshot.startDate,
        endDate: snapshot.endDate,
        progress: snapshot.progress,
        status: snapshot.status,
        operation: PpReportSnapshotOperation.DELETE,
      },
    },
    { manual: true, onSuccess }
  )

  const onClickMenu: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation()
    if (key === 'delete') {
      runDelete()
    } else {
      setTargetType(key)
    }
  }

  const renderExtras = (_: any, data: any) => {
    const d = data.project || data.portfolio || data.customer || data
    if (!isNil(d.status)) {
      if (!d.thisWeekReport) {
        return (
          <Button size='small' type='link' disabled>
            {t('no report')}
          </Button>
        )
      }
    }

    const selfId = d.objectiveId || d.selfId
    const type = d.objectiveId ? 2 : d.type
    const disabled = disabledSelfIds.map(String).includes(String(selfId))
    return (
      <Button
        size='small'
        type='link'
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation()
          runCreateLocked({ ...d, selfId, type })
        }}
      >
        {disabled ? t('已添加') : t('添加')}
      </Button>
    )
  }

  let title = ''
  let component = null
  switch (targetType) {
    case '项目': {
      title = t('add projects')
      component = <ProjectList renderExtras={renderExtras} />
      break
    }
    case '组合': {
      title = t('add portfolios')
      component = <PortfolioList renderExtras={renderExtras} />
      break
    }
    case '目标': {
      title = t('add objectives')
      component = <OKRList renderExtras={renderExtras} />
      break
    }
    case '团队': {
      title = t('add departments')
      component = <TeamList selfId={selfId} editable={false} renderExtras={renderExtras} />
      break
    }
    case '客户项目': {
      title = t('add customers')
      component = (
        <CustomerList canExport={false} isFullWidth={false} withParamPersist={false} renderExtras={renderExtras} />
      )
      break
    }
    case '产品项目': {
      title = t('add productions')
      component = (
        <ProductionList canExport={false} isFullWidth={false} withParamPersist={false} renderExtras={renderExtras} />
      )
      break
    }
    case '研发项目': {
      title = t('add devlops')
      component = (
        <DevlopList canExport={false} isFullWidth={false} withParamPersist={false} renderExtras={renderExtras} />
      )
      break
    }
  }

  let menus = [
    { key: 'delete', label: t('report.remove') },
    { key: '目标', label: t('add objectives') },
    { key: '项目', label: t('add projects') },
    { key: '组合', label: t('add portfolios') },
    { key: '团队', label: t('add departments') },
    { key: '客户项目', label: t('add customers'), disabled: true },
    { key: '产品项目', label: t('add productions'), disabled: true },
    { key: '研发项目', label: t('add devlops'), disabled: true },
  ]
  let whitelist: string[] = []
  if (depth === 0) {
    whitelist = fields.filter((field) => field.isChecked && !field.isDeleted).flatMap((field) => field.name.split('/'))
  } else {
    if (snapshot.type === ResourceType.PROJECT) {
      whitelist = ['delete']
    }
    if (snapshot.type === ResourceType.CUSTOMER) {
      whitelist = ['delete']
    }
    if (snapshot.type === ResourceType.PRODUCTION) {
      whitelist = ['delete']
    }
    if (snapshot.type === ResourceType.PORTFOLIO) {
      whitelist = ['项目', '组合', 'delete']
    }
    if (snapshot.type === ResourceType.OBJECTIVE) {
      whitelist = ['目标', '项目', '组合', 'delete']
    }
    if (snapshot.type === ResourceType.TEAM) {
      whitelist = ['目标', '项目', '组合', 'delete']
    }
  }
  if (whitelist.length > 0) {
    menus = menus.filter((m) => whitelist.includes(m.key))
  }

  return (
    <>
      <Dropdown
        trigger={['click']}
        overlay={
          <Menu onClick={onClickMenu}>
            {menus.map((menu) => (
              <Menu.Item key={menu.key} disabled={menu.disabled}>
                {menu.label}
              </Menu.Item>
            ))}
          </Menu>
        }
      >
        {children}
      </Dropdown>
      <Modal
        centered
        width='80vw'
        getContainer={document.body}
        title={title}
        footer={false}
        visible={!!targetType}
        onOk={() => {
          setTargetType(null)
        }}
        onCancel={() => {
          setTargetType(null)
        }}
      >
        {component}
      </Modal>
    </>
  )
}
